import { useEffect, useState } from "react";
import { PageHeader, StatCard, SectionCard, Pill } from "@/components/ui-bits";
import { CalendarClock, Trophy, Library, ClipboardList, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function StudentDashboard() {
  const { user } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [results, setResults] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/assessments").then(r => setAssessments(r.data));
    api.get("/results").then(r => setResults(r.data));
    if (user?.id) api.get(`/stats/student/${user.id}`).then(r => setStats(r.data));
  }, [user?.id]);

  const attemptsBy = results.reduce((acc, r) => {
    acc[r.assessment_id] = (acc[r.assessment_id] || 0) + 1;
    return acc;
  }, {});
  const upcoming = assessments.filter(a => {
    const attempts = attemptsBy[a.id] || 0;
    if (a.allow_reattempts === false && attempts >= 1) return false;
    if (a.max_attempts && attempts >= Number(a.max_attempts)) return false;
    return true;
  });

  return (
    <div>
      <PageHeader title={`Hi, ${user?.name?.split(" ")[0] || "Student"}.`} subtitle="Your courses, upcoming tests and certificates — all in one place." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Attempts" value={stats?.total_attempts ?? 0} icon={ClipboardList} accent="blue" testId="student-stat-attempts" />
        <StatCard label="Passed" value={stats?.passed ?? 0} icon={Trophy} accent="green" testId="student-stat-passed" />
        <StatCard label="Upcoming" value={upcoming.length} icon={CalendarClock} accent="amber" testId="student-stat-upcoming" />
        <StatCard label="Avg %" value={stats ? `${stats.avg_percentage}%` : "—"} icon={Library} accent="purple" testId="student-stat-avg" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        <SectionCard title="Next up" action={<Link to="/student/upcoming" className="text-sm text-blue-600 font-medium">View all</Link>}>
          {upcoming.length === 0 ? <div className="text-sm text-slate-500 py-6 text-center">All caught up.</div> : (
            <ul className="space-y-3">
              {upcoming.slice(0, 4).map(a => (
                <li key={a.id} className="border-b border-slate-100 pb-3 last:border-0">
                  <Link to={`/student/exam/${a.id}/intro`} data-testid={`student-upcoming-${a.id}`}
                    className="flex items-center justify-between gap-3 group">
                    <div>
                      <div className="text-sm font-medium text-slate-900 group-hover:text-blue-700">{a.title}</div>
                      <div className="text-xs text-slate-500">{a.duration} min · pass ≥ {a.passing_marks}%</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
        <SectionCard title="Recent results" action={<Link to="/student/results" className="text-sm text-blue-600 font-medium">View all</Link>}>
          {results.length === 0 ? <div className="text-sm text-slate-500 py-6 text-center">No results yet — take your first assessment.</div> : (
            <ul className="space-y-3">
              {results.slice(0, 4).map(r => (
                <li key={r.id} className="flex items-start justify-between gap-3 border-b border-slate-100 pb-3 last:border-0">
                  <div>
                    <div className="text-sm font-medium text-slate-900">{r.assessment_title}</div>
                    <div className="text-xs text-slate-500">{r.submitted_at?.slice(0,10)} · {Math.floor((r.time_taken||0)/60)}m</div>
                  </div>
                  <Pill tone={r.status === "passed" ? "green" : "red"}>{r.percentage}%</Pill>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
